import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Button from '../../components/Button/Button';
import { useReviewDetail } from '../../hooks/useReviewDetail';
import type { ReviewDetail } from '../../hooks/useReviewDetail';
import ReviewDialog from '../../components/ReviewDialog/ReviewDialog';
import EditableTable from '../../components/EditableTable/EditableTable';
import ProjectLanguageSelector from '../../components/ProjectLanguageSelector/ProjectLanguageSelector';
import EditKeyDialog from '../../components/EditKeyDialog/EditKeyDialog';
import TranslationKeyDialog from '../../components/TranslationKeyDialog/TranslationKeyDialog';
import LanguageCompletion from '../../components/LanguageCompletion/LanguageCompletion';
import { supabase } from '../../lib/supabase';
import { 
  fetchTranslationKeys,
  fetchTranslationKeyById,
  fetchTranslationStrings,
  updateTranslationKey,
  upsertTranslationString,
  deleteTranslationKey,
  createTranslationKey,
  checkKeyExists,
  TranslationKey,
  TranslationString 
} from '../../services/translationService';
import languagesData from '../../data/languages.json';
import './ReviewDetail.css';
// @ts-ignore
import arrowLeftIcon from '../../image-assets/icon.arrow.left.svg';
// @ts-ignore
import dropdownIcon from '../../image-assets/icon.arrows.dropdown.svg';
// @ts-ignore
import checkmarkIcon from '../../image-assets/icon.checkmark.svg';
// @ts-ignore
import editIcon from '../../image-assets/icon.edit.svg';
import { useLanguages } from '../../context/LanguageContext';
import { useNotification } from '../../context/NotificationContext';
import EmptyStateEnhanced from '../../components/EmptyStateEnhanced';
import { getReview, updateReview } from '../../services/reviewService';
import { useAuth } from '../../context/AuthContext';
import { useWorkspace } from '../../context/WorkspaceContext';
import { aiTranslationService } from '../../services/translationAI';
import ContentCard from '../../components/ContentCard/ContentCard';
import { animateTranslation } from '../../utils/translationAnimation';
import { setContentWithHighlightedPlaceholders } from '../../utils/placeholderUtils';

export interface ReviewDetailProps {
  id?: string;
  title?: string;
  owner?: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface TranslationData {
  key: TranslationKey;
  strings: TranslationString[];
}

const ReviewDetailComponent: React.FC<ReviewDetailProps> = (props) => {
  const { reviewId } = useParams<{ reviewId: string }>();
  const navigate = useNavigate();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const { review, isLoading, toggleReviewCompletion, updateReview, setReview } = useReviewDetail(reviewId);
  const [translationData, setTranslationData] = useState<TranslationData[]>([]);
  const [isLoadingKeys, setIsLoadingKeys] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [keyToEdit, setKeyToEdit] = useState<TranslationKey | null>(null);
  const [isEditKeyDialogOpen, setIsEditKeyDialogOpen] = useState(false);
  const [isEditingKey, setIsEditingKey] = useState(false);
  const { 
    selectedLanguage,
    firstLanguage,
    getLanguageName,
    setSelectedLanguage: handleLanguageChange,
    availableLanguages
  } = useLanguages();
  const [isKeyDialogOpen, setIsKeyDialogOpen] = useState(false);
  const [isCreatingKey, setIsCreatingKey] = useState(false);
  const { showNotification } = useNotification();
  const [translatingCells, setTranslatingCells] = useState<Set<string>>(new Set());
  const [isTranslatingAll, setIsTranslatingAll] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [shouldCancelTranslation, setShouldCancelTranslation] = useState(false);
  
  // Use props if provided, otherwise use review data
  const id = props.id || reviewId;
  const title = props.title || (review?.title);
  const owner = props.owner;
  const description = props.description || review?.description;
  const createdAt = props.createdAt || review?.created_at;
  const updatedAt = props.updatedAt || review?.last_updated_at;
  const isCompleted = review ? review.completed : false;
  
  // Set up the TopNavBar props when the component mounts or updates
  useEffect(() => {
    // Find the global event dispatcher to communicate with the main TopNavBar
    const setNavBarState = (state: any) => {
      const event = new CustomEvent('update-top-nav', { 
        detail: state
      });
      window.dispatchEvent(event);
    };
    
    // Set the review detail view state immediately for proper layout
    setNavBarState({
      onBackClickProp: handleClose,
      isReviewDetailViewProp: true,
      reviewId: id
    });
    
    // Only set the title when it's available
    if (title) {
      setNavBarState({
        reviewTitle: title,
        onBackClickProp: handleClose,
        isReviewCompleted: isCompleted,
        onEditClick: handleOpenEditDialog,
        onCompleteClick: handleToggleCompletion,
        isReviewDetailViewProp: true,
        reviewId: id
      });
    } else if (!review && !props.title && !isLoading) {
      // Only show "Review not found" when we're sure it's not found
      setNavBarState({
        reviewTitle: "Review not found",
        onBackClickProp: handleClose,
        isReviewDetailViewProp: true,
        reviewId: id
      });
    }
    
    // Clean up when component unmounts
    return () => {
      setNavBarState({
        isReviewDetailViewProp: false
      });
    };
  }, [title, isCompleted, isLoading, review, props.title]);
  
  // Load translation keys and their strings when review is loaded
  useEffect(() => {
    const loadKeys = async () => {
      if (!review?.keys) return;
      
      setIsLoadingKeys(true);
      try {
        // Fetch translation keys - handle the case where keys is a number or array
        const keyIds = Array.isArray(review.keys) ? review.keys : [review.keys];
        
        // Fetch each key and its strings
        const keysWithStrings = await Promise.all(
          keyIds.map(async (keyId) => {
            const key = await fetchTranslationKeyById(keyId);
            if (!key) return null;
            
            const strings = await fetchTranslationStrings(keyId);
            return { key, strings };
          })
        );
        
        // Filter out any null values and set the translation data
        const validData = keysWithStrings.filter((data): data is TranslationData => data !== null);
        setTranslationData(validData);
      } catch (err) {
        console.error('Error loading translation keys:', err);
      } finally {
        setIsLoadingKeys(false);
      }
    };
    
    loadKeys();
  }, [review]);
  
  // Handle opening the key dialog
  useEffect(() => {
    const handleOpenKeyDialog = (event: CustomEvent<{ reviewId: string }>) => {
      if (event.detail.reviewId === id) {
        setIsKeyDialogOpen(true);
      }
    };

    window.addEventListener('open-key-dialog', handleOpenKeyDialog as EventListener);
    return () => {
      window.removeEventListener('open-key-dialog', handleOpenKeyDialog as EventListener);
    };
  }, [id]);
  
  const handleClose = () => {
    // Navigate immediately without delay
    navigate('/app/reviews');
  };
  
  const handleToggleCompletion = async () => {
    if (reviewId && review) {
      await toggleReviewCompletion();
    }
  };
  
  const handleOpenEditDialog = () => {
    setIsEditDialogOpen(true);
  };
  
  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
  };
  
  const handleSaveEdit = async (title: string, description: string) => {
    if (review) {
      await updateReview({ title, description });
      setIsEditDialogOpen(false);
    }
  };
  
  // Handle cell changes in the table
  const handleCellChange = async (rowKey: string, columnKey: string, value: string) => {
    try {
      if (columnKey === 'key') {
        // Update key name
        const updatedKey = await updateTranslationKey(rowKey, value);
        if (updatedKey) {
          setTranslationData(prev => prev.map(data => 
            data.key.id === rowKey ? { ...data, key: updatedKey } : data
          ));
          showNotification('Key updated');
        }
      } else {
        // Update translation string using the actual language code
        if (!review?.project_id) {
          throw new Error('No project ID available');
        }
        const updatedString = await upsertTranslationString(rowKey, columnKey, value, review.project_id);
        if (updatedString) {
          setTranslationData(prev => prev.map(data => {
            if (data.key.id === rowKey) {
              const existingStringIndex = data.strings.findIndex(s => s.language_code === columnKey);
              const newStrings = [...data.strings];
              
              if (existingStringIndex >= 0) {
                newStrings[existingStringIndex] = updatedString;
              } else {
                newStrings.push(updatedString);
              }
              
              return { ...data, strings: newStrings };
            }
            return data;
          }));
          showNotification('Translation updated');
        }
      }
    } catch (err) {
      showNotification('Failed to update');
    }
  };

  // Handle AI translation for missing strings
  const handleAITranslate = async () => {
    if (!selectedLanguage || !review) return;

    // Find all empty cells that need translation
    const cellsToTranslate = translationData.filter(data => {
      const translation = data.strings.find(s => s.language_code === selectedLanguage);
      return !translation || !translation.value.trim();
    });

    if (cellsToTranslate.length === 0) {
      showNotification("No empty translations found");
      return;
    }

    // Show notification about starting the batch translation
    showNotification(`Translating ${cellsToTranslate.length} keys`);
    
    // Set flag to prevent navigation away
    setIsTranslating(true);
    
    // Track progress
    let translatedCount = 0;
    let errorCount = 0;
    
    // Process cells sequentially
    for (const item of cellsToTranslate) {
      // Check if translation was cancelled
      if (window.sessionStorage.getItem('texta_translation_cancelled')) {
        // Clean up UI for any cells that were in progress
        const cellElement = document.querySelector(`.editable-table-cell[data-row="${item.key.id}"][data-col="${selectedLanguage}"]`);
        if (cellElement) {
          cellElement.classList.remove('translating');
        }
        break;
      }
      
      try {
        // Apply translating class to the current cell
        const cellElement = document.querySelector(`.editable-table-cell[data-row="${item.key.id}"][data-col="${selectedLanguage}"]`);
        if (cellElement) {
          // Add the translating class
          cellElement.classList.add('translating');
          
          // Add a brief pause to allow the UI to update
          await new Promise(resolve => setTimeout(resolve, 500));
        }
        
        // Set the source language
        aiTranslationService.setSourceLanguage(firstLanguage || 'key');
        
        // Get source text from first language or key
        const sourceText = item.strings.find(s => s.language_code === firstLanguage)?.value || item.key.key;
        
        // Call the translation service
        const translatedText = await aiTranslationService.translateText(
          sourceText,
          selectedLanguage
        );
        
        // Check again if translation was cancelled before applying the result
        if (window.sessionStorage.getItem('texta_translation_cancelled')) {
          // Clean up UI for the current cell
          if (cellElement) {
            cellElement.classList.remove('translating');
          }
          break;
        }
        
        // Update the cell with the translated text
        if (translatedText && translatedText.trim()) {
          // Use the existing handleTableTranslate function to update the cell
          await handleTableTranslate(item.key.id, selectedLanguage, translatedText);
          translatedCount++;
          
          // Add a brief pause between translations to avoid rate limiting
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      } catch (error) {
        errorCount++;
        
        // Remove translating class from the failed cell
        const cellElement = document.querySelector(`.editable-table-cell[data-row="${item.key.id}"][data-col="${selectedLanguage}"]`);
        if (cellElement) {
          cellElement.classList.remove('translating');
        }
      }
    }
    
    // Complete the batch translation
    setIsTranslating(false);
    
    // Show final notification
    if (window.sessionStorage.getItem('texta_translation_cancelled')) {
      showNotification('Translation cancelled');
    } else if (errorCount > 0) {
      showNotification(`Translated ${translatedCount} cells with ${errorCount} errors`);
    } else {
      showNotification(`${translatedCount} translated`);
    }
  };

  // Handle editing a key
  const handleEditClick = (keyId: string) => {
    const keyData = translationData.find(data => data.key.id === keyId);
    if (keyData) {
      setKeyToEdit(keyData.key);
      setIsEditKeyDialogOpen(true);
    }
  };

  // Handle saving edited key name
  const handleSaveKeyEdit = async (newKeyName: string) => {
    if (!keyToEdit) return;
    
    try {
      setIsEditingKey(true);
      
      // Update the key name
      const updatedKey = await updateTranslationKey(keyToEdit.id, newKeyName);
      
      // Update the state with the new key name
      setTranslationData(prev => prev.map(data => 
        data.key.id === keyToEdit.id ? { ...data, key: updatedKey } : data
      ));

      showNotification('Key updated');
      setIsEditKeyDialogOpen(false);
      setKeyToEdit(null);
    } catch (err) {
      showNotification('Failed to update key');
      throw err;
    } finally {
      setIsEditingKey(false);
    }
  };

  // Handle deleting a key
  const handleDeleteKey = async () => {
    if (!keyToEdit) return;
    
    try {
      await deleteTranslationKey(keyToEdit.id);
      setTranslationData(prev => prev.filter(data => data.key.id !== keyToEdit.id));
      showNotification('Key deleted');
      setIsEditKeyDialogOpen(false);
      setKeyToEdit(null);
    } catch (err) {
      showNotification('Failed to delete key');
      throw err;
    }
  };

  // Handle row selection
  const handleRowSelect = (keyId: string, selected: boolean) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(keyId);
      } else {
        newSet.delete(keyId);
      }
      return newSet;
    });
  };

  // Get cell content based on column
  const getKeyCellContent = (row: TranslationData, columnKey: string) => {
    if (columnKey === 'key') {
      return row.key.key;
    }
    // Use the actual language code from the column key
    return row.strings.find(s => s.language_code === columnKey)?.value || '';
  };

  // Handle re-translate for a single cell
  const handleReTranslate = async (keyId: string, targetLanguage: string) => {
    try {
      // Find the source text
      const data = translationData.find(d => d.key.id === keyId);
      if (!data) return;

      const sourceString = data.strings.find(s => s.language_code === firstLanguage);
      if (!sourceString?.value) {
        showNotification('No source text found');
        return;
      }

      // Translate the text
      const translatedItem = await aiTranslationService.translateText(sourceString.value, targetLanguage);

      // Update the translation
      if (!review?.project_id) {
        throw new Error('No project ID available');
      }
      await handleCellChange(keyId, targetLanguage, translatedItem);

    } catch (error) {
      showNotification('Failed to re-translate');
    }
  };

  // Get cell actions based on column
  const getCellActions = (row: TranslationData, columnKey: string) => {
    if (columnKey === 'key' || columnKey === firstLanguage) {
      return undefined;
    }

    return [
      {
        label: 'Re-translate',
        onClick: () => handleReTranslate(row.key.id, columnKey)
      }
    ];
  };

  // Handle cell translation
  const handleTranslateCell = async (rowKey: string, columnKey: string, sourceText: string) => {
    try {
      const translatedText = await aiTranslationService.translateText(sourceText, columnKey);
      return { result: translatedText };
    } catch (error) {
      showNotification('Failed to translate');
      return null;
    }
  };

  // Define table columns
  const keyColumns = [
    { header: 'Key', key: 'key', editable: true },
    { header: getLanguageName(firstLanguage), key: firstLanguage || 'first', editable: true },
    { header: getLanguageName(selectedLanguage), key: selectedLanguage || 'second', editable: true }
  ];

  // Get container class based on navigation state
  const getContainerClass = () => {
    return 'review-detail-wrapper';
  };

  // Handle saving a new key
  const handleSaveKey = async (keyName: string, defaultValue: string) => {
    if (!review?.project_id) return;

    try {
      setIsCreatingKey(true);
      
      // Check if key already exists
      const keyExists = await checkKeyExists(review.project_id, keyName);
      if (keyExists) {
        throw new Error('A key with this name already exists in this project');
      }
      
      // Create the new key
      const newKey = await createTranslationKey(review.project_id, keyName);
      if (!newKey) throw new Error('Failed to create key');

      // Update the review's keys array
      const currentKeys = Array.isArray(review.keys) ? review.keys : [];
      const updatedKeys = [...currentKeys, newKey.id];

      const { error: updateError } = await supabase
        .from('Reviews')
        .update({ keys: updatedKeys })
        .eq('id', review.id);

      if (updateError) throw updateError;

      // If there's a default value, create the translation string
      if (defaultValue && firstLanguage) {
        if (!review?.project_id) {
          throw new Error('No project ID available');
        }
        await upsertTranslationString(newKey.id, firstLanguage, defaultValue, review.project_id);
      }

      // Fetch the new key's strings
      const strings = await fetchTranslationStrings(newKey.id);
      
      // Add the new key and its strings to the translation data
      setTranslationData(prev => [...prev, { key: newKey, strings }]);
      
      showNotification('Key added');
      setIsKeyDialogOpen(false);
    } catch (err) {
      showNotification('Failed to add key');
      throw err;
    } finally {
      setIsCreatingKey(false);
    }
  };

  const handleToggleLanguageComplete = async (languageCode: string) => {
    if (!reviewId || !review) return;

    try {
      const currentLanguages = review.languages_done || [];
      const isCompleted = currentLanguages.includes(languageCode);
      
      let newLanguages: string[];
      if (isCompleted) {
        // Remove the language
        newLanguages = currentLanguages.filter(lang => lang !== languageCode);
      } else {
        // Add the language
        newLanguages = [...currentLanguages, languageCode];
      }

      // Check if all languages are done
      const allLanguagesDone = availableLanguages.every(lang => 
        newLanguages.includes(lang)
      );

      // Update the review with the new languages_done array
      const { error } = await supabase
        .from('Reviews')
        .update({ languages_done: newLanguages })
        .eq('id', reviewId);

      if (error) throw error;

      // Update the review in local state
      setReview((prev: ReviewDetail | null) => {
        if (!prev) return null;
        return {
          ...prev,
          languages_done: newLanguages
        };
      });

      // If all languages are done, mark the task as complete
      if (allLanguagesDone) {
        await toggleReviewCompletion();
      }

      showNotification(isCompleted ? 'Language marked as incomplete' : 'Language marked as complete');
      if (allLanguagesDone) {
        showNotification('All languages completed! Task marked as done.');
      }
    } catch (error) {
      showNotification('Failed to update language completion status');
    }
  };

  // Watch for changes in review.completed and unmark all languages when task is reopened
  useEffect(() => {
    if (review && !review.completed) {
      // If task is reopened (not completed), clear all language completions
      const updateLanguages = async () => {
        try {
          const { error } = await supabase
            .from('Reviews')
            .update({ languages_done: [] })
            .eq('id', reviewId);

          if (error) throw error;

          // Update local state
          setReview((prev: ReviewDetail | null) => {
            if (!prev) return null;
            return {
              ...prev,
              languages_done: []
            };
          });

          showNotification('All languages unmarked');
        } catch (error) {
          showNotification('Failed to unmark languages');
        }
      };

      updateLanguages();
    }
  }, [review?.completed]);

  const handleCancelTranslation = () => {
    // Set a flag to stop translation
    window.sessionStorage.setItem('texta_translation_cancelled', 'true');
    
    // Reset the translating state
    setIsTranslating(false);
    
    // Remove translating class from all cells
    const cells = document.querySelectorAll('.editable-table-cell');
    cells.forEach(cell => {
      cell.classList.remove('translating');
    });
    
    // Clear the cancelled flag after a short delay
    setTimeout(() => {
      window.sessionStorage.removeItem('texta_translation_cancelled');
    }, 1000);
    
    showNotification('Translation cancelled');
  };

  // Calculate missing translations for the selected language
  const getMissingTranslationsCount = () => {
    if (!selectedLanguage || !translationData) return 0;
    return translationData.filter(row => {
      const translation = row.strings.find(s => s.language_code === selectedLanguage);
      return !translation || !translation.value.trim();
    }).length;
  };

  // Handle translate
  const handleTableTranslate = async (rowKey: string, columnKey: string, translatedText: string) => {
    
    if (!translatedText || !translatedText.trim()) {
      return;
    }
    
    // Find the row data
    const rowData = translationData.find(item => item.key.id === rowKey);
    
    if (rowData) {
      
      // Find and update the cell in the DOM
      const cellElement = document.querySelector(`.editable-table-cell[data-row="${rowKey}"][data-col="${columnKey}"]`);
      
      if (cellElement) {
        // Add translating class to show the magic border
        cellElement.classList.add('translating');
        
        // Set initial empty content before animation
        if (cellElement.getAttribute('contenteditable') === 'true') {
          cellElement.textContent = '';
        }
        
        // Create a unique animation effect using inline style with more pronounced animation
        const animateWords = (text: string) => {
          // Split by spaces but preserve spaces in the output
          const words = text.split(/\s+/);
          return words.map((word: string, index: number) => {
            // Calculate staggered delay based on word position
            const delay = 0.08 * index;
            // Make animation more dramatic for longer words
            const distance = Math.min(10 + word.length, 20);
            
            return `<span 
              class="word-animate" 
              style="
                animation-delay: ${delay}s; 
                animation-duration: 0.6s;
                display: inline-block; 
                opacity: 0; 
                transform: translateY(${distance}px);
                animation: fadeInUp 0.6s cubic-bezier(0.2, 0.8, 0.2, 1) ${delay}s forwards;
                position: relative;
              "
            >${word}</span>`;
          }).join(' ');
        };
        
        const animatedHTML = animateWords(translatedText);
        
        // Apply animated HTML immediately
        if (cellElement.getAttribute('contenteditable') === 'true') {
          cellElement.innerHTML = animatedHTML;
        }
        
        // For non-contenteditable elements, find and update child elements
        const cellContent = cellElement.querySelector('.cell-content');
        if (cellContent) {
          cellContent.innerHTML = animatedHTML;
        }
        
        // After the animation completes, set the actual text content for editing
        const animationDuration = Math.min(translatedText.split(/\s+/).length * 50 + 1500, 3500); // Cap at 3.5 seconds for long text
        
        // Create a reference to the element to use in the timeout
        const elementForTimeout = cellElement;
        
        setTimeout(() => {
          // Update with plain text after animation finishes
          if (elementForTimeout && elementForTimeout.getAttribute('contenteditable') === 'true') {
            elementForTimeout.textContent = translatedText;
            
            // Trigger data model update
            
            // Save changes to the data model
            handleCellChange(rowKey, columnKey, translatedText);
          }
          
          // Update spans as well
          if (elementForTimeout) {
            const spans = elementForTimeout.querySelectorAll('.cell-content > span:not(.word-animate)');
            spans.forEach(span => {
              span.textContent = translatedText;
            });
            
            // Also update the cell-content's text if there are no spans
            if (spans.length === 0) {
              const cellContent = elementForTimeout.querySelector('.cell-content');
              if (cellContent) {
                cellContent.textContent = translatedText;
                // Add placeholder highlighting after setting the text
                setContentWithHighlightedPlaceholders(cellContent as HTMLElement, translatedText);
              }
            }
          }
        }, animationDuration);
        
        // Store a reference to the element for the timeout
        const elementRef = cellElement;
        
        // Keep the animation visible for a bit, but not too long
        setTimeout(() => {
          if (elementRef) {
            elementRef.classList.remove('translating');
            
            // Ensure the text is still there after animation
            const contentEditableElement = elementRef.getAttribute('contenteditable') === 'true' ? 
              elementRef : 
              elementRef.querySelector('[contenteditable="true"]');
              
            if (contentEditableElement && !contentEditableElement.textContent) {
              contentEditableElement.textContent = translatedText;
            }
            
            // Check cell-content as well and add placeholder highlighting
            const cellContent = elementRef.querySelector('.cell-content');
            if (cellContent) {
              if (!cellContent.textContent) {
                cellContent.textContent = translatedText;
              }
              // Add placeholder highlighting after animation
              setContentWithHighlightedPlaceholders(cellContent as HTMLElement, translatedText);
            }
          }
        }, animationDuration + 1000);
        
        // Update the data model and database immediately
        
        await handleCellChange(rowKey, columnKey, translatedText);
        
        // Force a refresh of the UI to ensure consistency
        setTimeout(() => {
          // Update the translation data to trigger a re-render
          const updatedData = [...translationData];
          
          // Find and update the specific row/column
          const rowIndex = updatedData.findIndex(item => item.key.id === rowKey);
          if (rowIndex !== -1) {
            const stringIndex = updatedData[rowIndex].strings.findIndex(s => s.language_code === columnKey);
            
            // If string exists, update it; otherwise create a new one
            if (stringIndex !== -1) {
              updatedData[rowIndex].strings[stringIndex].value = translatedText;
            } else if (columnKey !== 'key') {
              // Add a new string if it doesn't exist
              updatedData[rowIndex].strings.push({
                id: `temp-${Date.now()}`, // Temporary ID
                key_id: rowKey,
                language_code: columnKey,
                value: translatedText,
                created_at: new Date().toISOString()
              });
            }
          }
          
          setTranslationData(updatedData);
          
          // Add placeholder highlighting after state update
          const cellContent = elementRef?.querySelector('.cell-content');
          if (cellContent) {
            setContentWithHighlightedPlaceholders(cellContent as HTMLElement, translatedText);
          }
        }, animationDuration + 200);
      } else {
        
        // Even if we can't find the cell, still update the data
        await handleCellChange(rowKey, columnKey, translatedText);
      }
    } else {
      
    }
  };

  // Don't render anything if data is still loading
  if (isLoading) {
    return <div className={getContainerClass()}></div>;
  }

  // Don't render content if review is not found
  if (!review && !props.title) {
    return (
      <div className={getContainerClass()}>
        <div className="review-detail-container">
          <div className="review-detail-content">
            <div className="review-detail-not-found">Review not found</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={getContainerClass()}>
      <div className="review-detail-container">
        <div className="review-detail-content">
          <div className="review-detail-main">
            {title && <h2 className="review-detail-name">{title}</h2>}
            
            {owner && <p className="review-detail-owner">Owned by {owner}</p>}
            
            {description && (
              <div className="review-detail-description">
                <p>{description}</p>
              </div>
            )}

             {/* Add Translate All card */}
             {(!selectedLanguage || selectedLanguage !== firstLanguage) && (
                    <ContentCard
                      title="Missing Translations"
                      subtitle={`${getMissingTranslationsCount()} keys missing translations in ${getLanguageName(selectedLanguage)}`}
                      buttonText="Translate All"
                      onButtonClick={handleAITranslate}
                      totalKeys={getMissingTranslationsCount()}
                      isComplete={getMissingTranslationsCount() === 0}
                      isLoading={isTranslating}
                      onCancel={handleCancelTranslation}
                    />
                  )}

            {/* Language Completion Section - Conditionally render based on keys */}
            {translationData.length > 0 && (
              <div className="review-detail-languages">
                <p className="review-detail-languages-title">Mark as done</p>
                <div className="review-detail-languages-pills">
                  {availableLanguages.map((languageCode: string) => (
                    <LanguageCompletion
                      key={languageCode}
                      languageCode={languageCode}
                      languageName={getLanguageName(languageCode)}
                      isCompleted={review?.languages_done?.includes(languageCode) || false}
                      onToggleComplete={handleToggleLanguageComplete}
                    />
                  ))}
                </div>
              </div>
            )}
            
            {/* Translation Keys Section */}
            <div className="review-detail-keys">
              {isLoadingKeys ? (
                <div className="loading-state">
                  <p>Loading translation keys...</p>
                </div>
              ) : translationData.length > 0 ? (
                <div className="translations-section">
                 
                  <EditableTable
                    columns={keyColumns}
                    data={translationData}
                    getRowKey={(row) => row.key.id}
                    getCellContent={getKeyCellContent}
                    getCellActions={getCellActions}
                    onCellChange={handleCellChange}
                    onDelete={handleEditClick}
                    selectedRows={selectedRows}
                    onRowSelect={handleRowSelect}
                    onTranslateCell={handleTranslateCell}
                    sourceLanguage={firstLanguage}
                    targetLanguage={selectedLanguage}
                    translatingCells={translatingCells}
                  />
                </div>
              ) : (
                <EmptyStateEnhanced
                  title="Add keys to review"
                  subtitle="Select existing keys or create new ones to start localizing content."
                  buttonText="Add key"
                  onButtonClick={() => setIsKeyDialogOpen(true)}
                  fullView={true}
                />
              )}
            </div>
            
            <div className="review-detail-metadata">
              {updatedAt && <p className="review-detail-timestamp">Last updated {updatedAt}</p>}
              {createdAt && <p className="review-detail-timestamp">Created {createdAt}</p>}
            </div>
          </div>
        </div>
      </div>
      
      {/* Add the ReviewDialog for editing */}
      {review && (
        <ReviewDialog 
          isOpen={isEditDialogOpen}
          onClose={handleCloseEditDialog}
          onSave={handleSaveEdit}
          isLoading={isLoading}
          initialTitle={review.title}
          initialDescription={review.description}
        />
      )}

      {/* Add the EditKeyDialog for editing keys */}
      {keyToEdit && (
        <EditKeyDialog
          isOpen={isEditKeyDialogOpen}
          onClose={() => {
            setIsEditKeyDialogOpen(false);
            setKeyToEdit(null);
          }}
          onSave={handleSaveKeyEdit}
          onDelete={handleDeleteKey}
          keyName={keyToEdit.key}
          isLoading={isEditingKey}
        />
      )}

      {/* Add the ProjectLanguageSelector at the bottom left */}
      <ProjectLanguageSelector onLanguageSelect={handleLanguageChange} />

      {/* Add the TranslationKeyDialog */}
      <TranslationKeyDialog
        isOpen={isKeyDialogOpen}
        onClose={() => setIsKeyDialogOpen(false)}
        onSave={handleSaveKey}
        isLoading={isCreatingKey}
        firstLanguage={firstLanguage}
      />
    </div>
  );
};

export default ReviewDetailComponent; 